import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useOfflineStorage } from '@/hooks/useOfflineStorage';
import { prepareFileForStorage, canStoreOffline, MAX_OFFLINE_FILE_SIZE, formatFileSize } from '@/utils/offlineFileHandler';

export interface DocumentoLegal {
  id: string;
  user_id: string;
  nombre_documento: string;
  nombre_archivo: string;
  tipo_documento: 'Autos de Seguimientos' | 'Actas de Oralidad de Seguimiento' | 'Resoluciones';
  url_archivo: string;
  ruta_storage: string;
  tamano_archivo: number;
  created_at: string;
  updated_at: string;
}

export const useDocumentosLegales = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { cacheData, getCachedData, addPendingOperation, isOnline } = useOfflineStorage();
  const [documentos, setDocumentos] = useState<DocumentoLegal[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const loadDocumentos = async () => {
    if (!user) return;

    const cacheKey = `documentos_legales_${user.id}`;

    try {
      setLoading(true);
      
      if (!isOnline) {
        // Cargar desde caché offline
        const cached = await getCachedData('documentos', cacheKey);
        if (cached) {
          setDocumentos(cached);
        }
        return;
      }

      const { data, error } = await supabase
        .from('documentos_legales')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const docs = (data || []) as DocumentoLegal[];
      setDocumentos(docs);
      
      // Actualizar caché
      await cacheData('documentos', cacheKey, docs);
    } catch (error) {
      console.error('Error loading documentos:', error);
      
      // Si falla y estamos online, intentar cargar desde caché
      const cached = await getCachedData('documentos', cacheKey);
      if (cached) {
        setDocumentos(cached);
        toast({
          title: "Modo offline",
          description: "Mostrando documentos guardados localmente",
        });
      } else {
        toast({
          title: "Error",
          description: "No se pudieron cargar los documentos",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const uploadDocumento = async (
    file: File,
    nombreDocumento: string,
    tipoDocumento: DocumentoLegal['tipo_documento']
  ): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Error",
        description: "Debes estar autenticado para subir documentos",
        variant: "destructive",
      });
      return false;
    }

    if (file.type !== 'application/pdf') {
      toast({
        title: "Error",
        description: "Solo se permiten archivos PDF",
        variant: "destructive",
      });
      return false;
    }

    // Validar tamaño para modo offline
    if (!isOnline && !canStoreOffline(file)) {
      toast({
        title: "Archivo muy grande",
        description: `Para subir archivos mayores a ${formatFileSize(MAX_OFFLINE_FILE_SIZE)}, conéctate a internet`,
        variant: "destructive",
      });
      return false;
    }

    try {
      setUploading(true);
      const timestamp = Date.now();
      const tempId = `temp_${timestamp}`;

      if (!isOnline) {
        // Modo offline: convertir a base64 y guardar
        const fileData = await prepareFileForStorage(file);
        if (!fileData) {
          throw new Error('No se pudo preparar el archivo');
        }

        const tempDoc: DocumentoLegal = {
          id: tempId,
          user_id: user.id,
          nombre_documento: nombreDocumento,
          nombre_archivo: file.name,
          tipo_documento: tipoDocumento,
          url_archivo: 'pending',
          ruta_storage: 'pending',
          tamano_archivo: file.size,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // Agregar a operaciones pendientes
        await addPendingOperation('create', 'documentos_legales', {
          ...tempDoc,
          fileData,
        });

        // Actualizar UI inmediatamente
        setDocumentos(prev => [tempDoc, ...prev]);

        toast({
          title: "Documento guardado offline",
          description: "Se subirá cuando te conectes a internet",
        });

        return true;
      }

      // Modo online: flujo normal
      const fileName = `${timestamp}-${file.name}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documentos-legales')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('documentos-legales')
        .getPublicUrl(filePath);

      const { error: dbError } = await supabase
        .from('documentos_legales')
        .insert({
          user_id: user.id,
          nombre_documento: nombreDocumento,
          nombre_archivo: file.name,
          tipo_documento: tipoDocumento,
          url_archivo: publicUrl,
          ruta_storage: filePath,
          tamano_archivo: file.size
        });

      if (dbError) throw dbError;

      toast({
        title: "Documento subido",
        description: "El documento se ha subido correctamente",
      });

      await loadDocumentos();
      return true;
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({
        title: "Error",
        description: "No se pudo subir el documento",
        variant: "destructive",
      });
      return false;
    } finally {
      setUploading(false);
    }
  };

  const deleteDocumento = async (documento: DocumentoLegal): Promise<boolean> => {
    try {
      if (!isOnline) {
        // Modo offline: marcar como eliminado
        await addPendingOperation('delete', 'documentos_legales', {
          id: documento.id,
        });

        // Actualizar UI inmediatamente
        setDocumentos(prev => prev.filter(d => d.id !== documento.id));

        toast({
          title: "Documento marcado para eliminar",
          description: "Se eliminará cuando te conectes a internet",
        });

        return true;
      }

      // Modo online: flujo normal
      const { error: storageError } = await supabase.storage
        .from('documentos-legales')
        .remove([documento.ruta_storage]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('documentos_legales')
        .delete()
        .eq('id', documento.id);

      if (dbError) throw dbError;

      toast({
        title: "Documento eliminado",
        description: "El documento se ha eliminado correctamente",
      });

      await loadDocumentos();
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el documento",
        variant: "destructive",
      });
      return false;
    }
  };

  const getEstadisticas = () => {
    const total = documentos.length;
    const autosSeguimientos = documentos.filter(d => d.tipo_documento === 'Autos de Seguimientos').length;
    const actasOralidad = documentos.filter(d => d.tipo_documento === 'Actas de Oralidad de Seguimiento').length;
    const resoluciones = documentos.filter(d => d.tipo_documento === 'Resoluciones').length;

    return {
      total,
      autosSeguimientos,
      actasOralidad,
      resoluciones
    };
  };

  useEffect(() => {
    if (user) {
      loadDocumentos();
    }
  }, [user]);

  // Subscribe to real-time changes for ALL users
  useEffect(() => {
    if (!user) return;

    console.log('Setting up realtime subscription for documentos_legales (all users)');
    
    const channel = supabase
      .channel('documentos_legales_all_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'documentos_legales'
        },
        (payload) => {
          console.log('Documentos legales realtime change (all users):', payload);
          // Reload documents for all changes
          loadDocumentos();
        }
      )
      .subscribe((status) => {
        console.log('Documentos legales subscription status:', status);
      });

    return () => {
      console.log('Cleaning up documentos_legales realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [user]);

  return {
    documentos,
    loading,
    uploading,
    uploadDocumento,
    deleteDocumento,
    loadDocumentos,
    getEstadisticas
  };
};